import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  PageDescription,
  ContentGrid,
  Card,
  CardTitle,
  CardContent,
  ActionBar,
  Button,
  SearchInput,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  StatusBadge,
  TabContainer,
  TabList,
  Tab,
  TabContent,
  fadeIn,
  formatDate
} from '../styles/PageStyles';
import styled, { keyframes } from 'styled-components';
import { faNetworkWired, faCogs, faWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageHeaderWithInfo from '../components/common/PageHeaderWithInfo';
import { getPipelineCount, getPipelines } from '../services/pipelineService';
import { getAgents } from '../services/agentService';
import Pagination from '../components/common/Pagination'; // Add this import

// Styled components for the pipeline page
const MetricsContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const MetricCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  flex: 1;
  min-width: 200px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const MetricValue = styled.div`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-primary);
`;

const MetricLabel = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const MetricChange = styled.div`
  font-size: 0.8rem;
  color: ${props => props.positive ? '#34C759' : props.negative ? '#FF3B30' : 'var(--text-secondary)'};
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? 'var(--primary)' : 'white'};
  color: ${props => props.active ? 'white' : 'var(--text-primary)'};
  border: 1px solid ${props => props.active ? 'var(--primary)' : '#e0e0e0'};
  border-radius: 20px;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active ? 'var(--primary)' : 'rgba(0, 102, 255, 0.05)'};
  }
`;

const PipelineCard = styled(Card)`
  margin-bottom: 1.5rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

const PipelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const PipelineTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PipelineTypeIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.6rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.type === 'tool' ? '#fff5e6' : '#e3f2fd'};
  color: ${props => props.type === 'tool' ? '#ff9500' : '#1976d2'};
  border: 1px solid ${props => props.type === 'tool' ? '#ffcc80' : '#bbdefb'};
`;

const PipelineStatus = styled.div`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  font-size: 0.85rem;
  color: var(--text-primary);
  background: white;  
  margin-left:5px;
`;

const PipelineTags = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const PipelineTag = styled.div`
  font-size: 0.8rem;
  padding: 0.2rem 0.6rem;
  border-radius: 4px;
  background: rgba(0, 102, 255, 0.05);
  color: var(--primary);
`;

const PipelineDescription = styled.p`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
  line-height: 1.5;
`;

const AgentsList = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const AgentItem = styled.div`

  font-size: 0.8rem;
  padding: 0.3rem 0.8rem;
  border-radius: 20px;
  background: ${props => props.active ? 'rgba(52, 199, 89, 0.1)' : 'rgba(142, 142, 147, 0.1)'};
  color: ${props => props.active ? '#34C759' : 'var(--text-secondary)'};
`;

// const PipelineMetrics = styled.div`
//   display: flex;
//   gap: 1rem;
//   margin-bottom: 1rem;
//   flex-wrap: wrap;
// `;

// const PipelineMetric = styled.div`
//   flex: 1;
//   min-width: 100px;
// `;

// const PipelineMetricValue = styled.div`
//   font-size: 1.2rem;
//   font-weight: 600;
//   color: var(--text-primary);
// `;

// const PipelineMetricLabel = styled.div`
//   font-size: 0.8rem;
//   color: var(--text-secondary);
// `;

const PipelineFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid #f0f0f0;
  font-size: 0.85rem;
  color: var(--text-secondary);
`;

const PipelineActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;


const Spinner = styled.div`
  border: 4px solid #f3f3f3;   
  border-top: 4px solid #0066ff; 
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
  margin: auto;
`;

const AgentPipelinesV2 = () => {
  const [searchTerm, setSearchTerm] = useState('');
  // const [activeTab, setActiveTab] = useState('all');
  const [activeFilter, setActiveFilter] = useState('all');
  const [pipelineList, setPipelineList] = useState([]);
  const [agentList, setAgentList] = useState([]);
  const navigate = useNavigate();

  // Add pagination state variables
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const itemsPerPage = 3;
  const observer = useRef(null);

  const loaderRef = useCallback(node => {
  if (!node) return;

  if (observer.current) observer.current.disconnect();

  observer.current = new IntersectionObserver(entries => {
    if (entries[0].isIntersecting && hasMore && !paginationLoading && !loading) {
      setPaginationLoading(true);
      setTimeout(() => setCurrentPage(prev => prev + 1), 300);
    }
  }, {
    rootMargin: "100px",
    threshold: 0.1
  });

  observer.current.observe(node);

  }, [hasMore, paginationLoading, loading, setCurrentPage]);


  useEffect(() => {

    const getAllPipelines = async () => {
      const isInitialLoad = currentPage === 1;
      
      if (isInitialLoad) {
        setLoading(true);
      }
      
      try {
        const queryParams = `skip=${(currentPage - 1) * itemsPerPage}&limit=${itemsPerPage}`;
        const resp = await getPipelines({}, {}, queryParams);
        
        // Check if response is empty or has fewer items than requested
        const responseArray = Array.isArray(resp) ? resp : [];
        
        if (responseArray.length === 0 || responseArray.length < itemsPerPage) {
          console.log("Reached the end of data, no more to load");
          setHasMore(false);
        }

       // Commented out temporarily — will re-enable after verification.
        // let filteredPipelines = responseArray.filter((pipe) => {
        //   const name = pipe?.name?.toLowerCase() || "";
        //   const description = pipe?.description?.toLowerCase() || "";

        //   return !name.includes("test") && !description.includes("test");
        // });

        setPipelineList(prev => [...prev, ...responseArray]);
      } catch (error) {
        console.log("Error while fetching the pipelines", error);
        // Set hasMore to false on error to prevent infinite retries
        setHasMore(false);
      } finally {
        setLoading(false);
        setPaginationLoading(false);
      }
    };

    const getAllAgentList = async () => {
      try {
        const list = await getAgents();
        setAgentList(list || []);
      } catch (error) {
        console.log("Error while fetching agents", error);
      }
    };

    getAllPipelines();
    if (currentPage === 1) {
      getAllAgentList();
    }
  }, [currentPage, itemsPerPage]);


 useEffect(() => {
    const fetchAndSetPipelineCount = async () => {
      try {
        const count = await getPipelineCount();
        setTotalItems(count);
        
        // Only set hasMore to true if we're at the beginning
        // Let the API response handler determine when to set it to false
        if (currentPage === 1) {
          setHasMore(pipelineList.length < count);
        }
      } catch (error) {
        console.log("Error while fetching pipeline data", error);
      }
    };

    // Only fetch count once at the beginning or when searching/filtering
    if (currentPage === 1) {
      fetchAndSetPipelineCount();
    }
  }, [currentPage, searchTerm, activeFilter]);

  const pipelines = [];

  const filteredPipelines = pipelineList.filter(pipeline => {
    const matchesSearch =
      pipeline.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pipeline.description.toLowerCase().includes(searchTerm.toLowerCase());

    if (activeFilter === 'all') return matchesSearch;
    return matchesSearch && pipeline.tags.some(tag => tag.toLowerCase() === activeFilter.toLowerCase());
  });

  // Calculate summary metrics
  const activePipelines = pipelines.filter(p => p.status === 'active').length;
  const totalProcessed = pipelines.reduce((sum, p) => sum + parseInt(p.metrics.processed.replace(/,/g, '')), 0);
  const formattedTotalProcessed = totalProcessed.toLocaleString();

  // Calculate average processing time (simplified for demo)
  const avgCompletionTime = '1.2d';

  // Calculate average success rate
  const avgSuccessRate = pipelines.reduce((sum, p) => {
    return sum + parseFloat(p.metrics.successRate.replace('%', ''));
  }, 0) / pipelines.length;
  const formattedSuccessRate = avgSuccessRate.toFixed(1) + '%';
  const renderAgentsForPipeline = (pipeline) => {
    if (agentList && agentList.length > 0) {
      // Filter out null or undefined agent codes
      const validAgentCodes = pipeline.nodes
        .map((item) => item.agent_code)
        .filter((code) => code !== null && code !== undefined);

      // Find matching agent names from the agentList
      const matchingAgents = validAgentCodes
        .map((code) => {
          const agent = agentList.find((agent) => agent.code === code);
          return agent ? agent.name : null; 
        })
        .filter((name) => name !== null); 
      return (
        <>
        {matchingAgents && matchingAgents.length > 0 ? (
          matchingAgents.map((agentName, index) => (
            <AgentItem key={index}>{agentName}</AgentItem>
          ))
        ) : (
          <AgentItem>No Agents Found</AgentItem>
        )}
      </>
      );
    }

    return <AgentItem>No Agents Found</AgentItem>;
  };
  return (
  <PageContainer>
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeIn}
    >
      <PageHeaderWithInfo
        icon={faNetworkWired}
        title="Agent Pipelines"
        description={null}
      >
        <div style={{ display: 'flex', gap: '1rem' }}>
          <SearchInput
            type="text"
            placeholder="Search pipelines..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <Button>Clone</Button>
          <Button primary onClick={() => navigate('/pipeline-designer/new')}>Create Pipeline</Button>
        </div>
      </PageHeaderWithInfo>

      {/* <MetricsContainer>
        <MetricCard>
          <MetricLabel>Active Pipelines</MetricLabel>
          <MetricValue>{activePipelines}</MetricValue>
          <MetricChange positive>+2 this month</MetricChange>
        </MetricCard>

        <MetricCard>
          <MetricLabel>Claims Processed</MetricLabel>
          <MetricValue>{formattedTotalProcessed}</MetricValue>
          <MetricChange positive>12% increase</MetricChange>
        </MetricCard>

        <MetricCard>
          <MetricLabel>Average Completion Time</MetricLabel>
          <MetricValue>{avgCompletionTime}</MetricValue>
          <MetricChange positive>15% faster</MetricChange>
        </MetricCard>

        <MetricCard>
          <MetricLabel>Success Rate</MetricLabel>
          <MetricValue>80%</MetricValue>
          <MetricChange positive>2.3% improvement</MetricChange>
        </MetricCard>
      </MetricsContainer> */}

      {/* Pipeline Cards Container */}
      <div>
        {(loading) && (
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem',
            color: 'var(--text-secondary)',
            display:'flex',
            flexDirection:'column',
            gap:'14px',
            padding:'10px 0'
          }}>
            <Spinner />
            Loading pipelines...
          </div>
        )}

        {!loading && !paginationLoading && pipelineList.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem',
            color: 'var(--text-secondary)'
          }}>
            No pipelines found.
          </div>
        )}

        { pipelineList.length > 0 &&
          filteredPipelines.map(pipeline => (
            <PipelineCard key={pipeline.id}>
              <PipelineHeader>
                <PipelineTitle>
                  <PipelineTypeIndicator type={pipeline.type || 'pipeline'}>
                    <FontAwesomeIcon 
                      icon={pipeline.type === 'tool' ? faWrench : faCogs} 
                    />
                    {pipeline.type === 'tool' ? 'Tool' : 'Pipeline'}
                  </PipelineTypeIndicator>
                  {pipeline.name}
                  <PipelineStatus active={true}>
                    Active
                  </PipelineStatus>
                </PipelineTitle>
                <AgentsList>
                  {renderAgentsForPipeline(pipeline)}
                </AgentsList>
              </PipelineHeader>

              <PipelineDescription>
                {pipeline.description}
              </PipelineDescription>

              

              <PipelineFooter>
                <div style={{ display: 'flex', gap: '2rem' }}>
                  <div>Last updated: {formatDate(pipeline.updated_at)} By {pipeline.updated_by}</div>
                  {pipeline.created_by && <div>Owner: {pipeline.created_by}</div>}
                  {pipeline?.code && <div>Code: {pipeline.code}</div>}
                </div>

                <PipelineActions>
                  <Button onClick={(e)=>{navigate(`/pipeline-analytics/${pipeline.code}`)}}>View Analytics</Button>
                  <Button primary onClick={(e)=>{navigate(`/pipeline-designer/${pipeline.code}`)}}>Manage</Button>
                </PipelineActions>
              </PipelineFooter>
            </PipelineCard>
          ))}
      </div>
      
      {/* Infinite scroll loading indicator */}
      {paginationLoading}
      {hasMore && !loading && (
        <div 
          ref={loaderRef}
          style={{ 
            textAlign: 'center', 
            padding: '1rem',
            margin: '1rem 0',
            height: '82px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          {paginationLoading && <div 
            style={{
              display:'flex',
              flexDirection:'column',
              gap:'14px',
              padding:'10px 0'
            }}
          >
            <Spinner />
            <span>Loading more pipelines...</span>
          </div>}
        </div>
      )}
      
      {/* Show when all content is loaded */}
      {!hasMore && pipelineList.length > 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '1rem',
            color: 'var(--text-secondary)',
            margin: '1rem 0'
          }}
        >
          You've reached the end of the list
        </div>
      )}

    </motion.div>
  </PageContainer>
);
};

export default AgentPipelinesV2;
